import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import LessonContent from "../components/LessonContent";
import { toast } from "sonner";
import {
  BookOpen,
  Clock,
  Users,
  GraduationCap,
  PlayCircle,
  FileText,
  HelpCircle,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Globe,
  Lock
} from "lucide-react";

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:00`;
};

const getLessonIcon = (type) => {
  const icons = { video: PlayCircle, text: FileText, quiz: HelpCircle, assignment: ClipboardList };
  return icons[type] || FileText;
};

const getLevelColor = (level) => {
  const colors = {
    beginner: "bg-[#10B981]/10 text-[#059669] border-[#10B981]/20",
    intermediate: "bg-[#F59E0B]/10 text-[#D97706] border-[#F59E0B]/20",
    advanced: "bg-[#EF4444]/10 text-[#DC2626] border-[#EF4444]/20"
  };
  return colors[level] || colors.beginner;
};

const getAccessBadge = (type) => {
  if (type === "soon") return <Badge className="bg-slate-100 text-slate-500 border border-slate-200 text-xs">Coming Soon</Badge>;
  if (type === "premium") return <Badge className="bg-brand-gold/10 text-brand-navy border border-brand-gold/20 text-xs"><Lock className="w-3 h-3 mr-1" />Premium</Badge>;
  return null;
};

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [activeLesson, setActiveLesson] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const contentRes = await fetch(`${API}/courses/${courseId}/content`, { credentials: "include" });
        if (contentRes.ok) {
          setCourse(await contentRes.json());
        } else {
          toast.error("Course not found");
          navigate("/courses");
          return;
        }

        if (user) {
          const enrollRes = await fetch(`${API}/enrollments/me`, { credentials: "include" });
          if (enrollRes.ok) {
            const enrollments = await enrollRes.json();
            setIsEnrolled(enrollments.some((e) => e.course_id === courseId));
          }
        }
      } catch (error) {
        console.error("Error fetching course:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [courseId, navigate, user]);

  useEffect(() => {
    if (course?.modules) {
      const initial = {};
      course.modules.forEach((m, i) => {
        if (m.access_type !== "soon") initial[m.module_id] = true;
      });
      setExpandedModules(initial);
    }
  }, [course]);

  const handleEnroll = async () => {
    if (!user) { login(); return; }
    setEnrolling(true);
    try {
      const response = await fetch(`${API}/course-enrollments?course_id=${courseId}`, {
        method: "POST", credentials: "include"
      });
      if (response.ok) {
        toast.success("Successfully enrolled!");
        setIsEnrolled(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to enroll");
      }
    } catch { toast.error("An error occurred"); }
    finally { setEnrolling(false); }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-app">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-4 border-brand-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!course) return null;

  const totalLessons = course.modules?.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-surface-app" data-testid="course-detail-page">
      <Header />

      {/* Hero Section */}
      <section className="bg-brand-navy pt-28 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#152C6E] to-[#0F1D4A]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/courses" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-courses-link">
            <ArrowLeft className="w-4 h-4" /> Back to Courses
          </Link>

          <div className="max-w-3xl">
            {course.partner_label && (
              <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3" data-testid="course-partner-label">
                {course.partner_label}
              </p>
            )}
            <Badge className={`${getLevelColor(course.skill_level)} border mb-4 capitalize`}>
              {course.skill_level}
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6 tracking-tight leading-tight" data-testid="course-title">
              {course.title}
            </h1>
            <p className="text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              {course.description}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-white/70">
              {course.instructor_name && (
                <span className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-brand-navy" />
                  {course.instructor_name}
                </span>
              )}
              <span className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-brand-navy" />
                {course.modules?.length || 0} sections, {totalLessons} lessons
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-navy" />
                {course.duration_weeks} weeks
              </span>
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-brand-navy" />
                {course.current_enrollment}/{course.max_enrollment} enrolled
              </span>
            </div>

            <div className="mt-8">
              {isEnrolled ? (
                <Link to={`/courses/${courseId}/learn`}>
                  <Button className="bg-brand-gold hover:bg-[#FCC100] text-white px-8 py-3 rounded-xl font-semibold text-base shadow-lg shadow-brand-gold/20" data-testid="start-learning-btn">
                    <PlayCircle className="w-5 h-5 mr-2" /> Start Learning
                  </Button>
                </Link>
              ) : course.current_enrollment < course.max_enrollment ? (
                <Button
                  onClick={handleEnroll}
                  disabled={enrolling}
                  className="bg-brand-gold hover:bg-[#FCC100] text-white px-8 py-3 rounded-xl font-semibold text-base shadow-lg shadow-brand-gold/20"
                  data-testid="enroll-btn"
                >
                  {enrolling ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> Enrolling...</>
                  ) : (
                    "Register"
                  )}
                </Button>
              ) : (
                <Button disabled className="bg-slate-500 text-white px-8 py-3 rounded-xl opacity-60 cursor-not-allowed">
                  Course Full
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Course Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main: Modules & Lessons */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-6" data-testid="course-lessons-heading">
              Course Lessons
            </h2>

            <div className="space-y-4">
              {course.modules?.length > 0 ? (
                course.modules.map((module, idx) => {
                  const isExpanded = expandedModules[module.module_id];
                  const isSoon = module.access_type === "soon";

                  return (
                    <div
                      key={module.module_id}
                      className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden"
                      data-testid={`module-section-${module.module_id}`}
                    >
                      {/* Module Header */}
                      <button
                        onClick={() => !isSoon && toggleModule(module.module_id)}
                        className={`w-full flex items-center gap-4 p-5 text-left transition-colors ${isSoon ? "opacity-60 cursor-default" : "hover:bg-slate-50"}`}
                        data-testid={`module-toggle-${module.module_id}`}
                      >
                        <div className="w-10 h-10 bg-brand-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-brand-navy font-bold text-sm">
                            {String(idx + 1).padStart(2, "0")}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-slate-900 text-base">{module.title}</h3>
                          {module.description && (
                            <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{module.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {getAccessBadge(module.access_type)}
                          {!isSoon && (
                            isExpanded
                              ? <ChevronUp className="w-5 h-5 text-slate-400" />
                              : <ChevronDown className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </button>

                      {/* Lessons List */}
                      {isExpanded && !isSoon && module.lessons?.length > 0 && (
                        <div className="border-t border-slate-100 px-5 pb-4">
                          {module.lessons.map((lesson) => {
                            const Icon = getLessonIcon(lesson.lesson_type);
                            const duration = formatDuration(lesson.duration_minutes);
                            return (
                              <button
                                key={lesson.lesson_id}
                                type="button"
                                onClick={() => !isSoon && setActiveLesson(lesson)}
                                disabled={isSoon}
                                className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0 w-full text-left disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 -mx-2 px-2 rounded transition"
                                data-testid={`lesson-row-${lesson.lesson_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <Icon className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm text-slate-700">{lesson.title}</span>
                                  {isSoon && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Soon</span>}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-400">
                                  {lesson.content?.duration_display && <span>{lesson.content.duration_display}</span>}
                                  {!lesson.content?.duration_display && duration && <span>{duration}</span>}
                                  {lesson.pages > 0 && <span>{lesson.pages} Pages</span>}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {isExpanded && !isSoon && (!module.lessons || module.lessons.length === 0) && (
                        <div className="border-t border-slate-100 px-5 py-6 text-center">
                          <p className="text-sm text-slate-400">Lessons coming soon</p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center">
                  <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Course content is being prepared.</p>
                </div>
              )}
            </div>

            {/* Resources */}
            {course.resources?.length > 0 && (
              <div className="mt-10" data-testid="course-resources-section">
                <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">
                  Resources
                </h2>
                <ul className="space-y-2">
                  {course.resources.map((r, i) => (
                    <li
                      key={i}
                      className="bg-white border border-slate-200/60 rounded-xl px-5 py-3 flex items-center gap-3"
                      data-testid={`course-resource-${i}`}
                    >
                      <FileText className="w-4 h-4 text-brand-navy flex-shrink-0" />
                      <span className="text-sm text-slate-800 flex-1">{r.title}</span>
                      {r.pages > 0 && <span className="text-xs text-slate-400">{r.pages} {r.pages === 1 ? "Page" : "Pages"}</span>}
                      {r.type === "link" && <span className="text-xs text-slate-400">Link</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar: Topics + Quick Stats */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Enroll Card */}
              <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
                <div className="w-14 h-14 bg-brand-gold/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <BookOpen className="w-7 h-7 text-brand-navy" />
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Duration</span>
                    <span className="text-sm font-medium text-slate-900">{course.duration_weeks} weeks</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Sections</span>
                    <span className="text-sm font-medium text-slate-900">{course.modules?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Lessons</span>
                    <span className="text-sm font-medium text-slate-900">{totalLessons}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                    <span className="text-sm text-slate-500">Enrolled</span>
                    <span className="text-sm font-medium text-slate-900">{course.current_enrollment}/{course.max_enrollment}</span>
                  </div>
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-sm text-slate-500">Level</span>
                    <span className="text-sm font-medium text-slate-900 capitalize">{course.skill_level}</span>
                  </div>
                </div>

                {isEnrolled ? (
                  <Link to={`/courses/${courseId}/learn`} className="block">
                    <Button className="w-full bg-brand-navy hover:bg-[#152C6E] text-white rounded-xl" data-testid="sidebar-start-btn">
                      <PlayCircle className="w-4 h-4 mr-2" /> Continue Learning
                    </Button>
                  </Link>
                ) : course.current_enrollment < course.max_enrollment ? (
                  <Button onClick={handleEnroll} disabled={enrolling} className="w-full bg-brand-gold hover:bg-[#FCC100] text-white rounded-xl" data-testid="sidebar-enroll-btn">
                    {enrolling ? "Enrolling..." : "Register"}
                  </Button>
                ) : (
                  <Button disabled className="w-full opacity-50 cursor-not-allowed">Course Full</Button>
                )}
                <p className="text-center text-xs text-slate-400 mt-3">
                  {isEnrolled ? "You're enrolled in this course" : "Free enrollment for Academy participants"}
                </p>
              </div>

              {/* Topics */}
              {course.topics?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
                  <h3 className="font-semibold text-slate-900 mb-4">What You'll Learn</h3>
                  <ul className="space-y-2.5">
                    {course.topics.map((topic, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <div className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Instructor Section */}
        {course.instructor_name && (
          <div className="mt-16 border-t border-slate-200 pt-12" data-testid="instructor-section">
            <div className="max-w-3xl">
              <div className="flex items-start gap-6">
                {course.instructor_photo_url ? (
                  <img
                    src={course.instructor_photo_url}
                    alt={course.instructor_name}
                    className="w-20 h-20 rounded-full object-cover border-2 border-brand-gold/30 flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-brand-navy flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-9 h-9 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-1">
                    {course.instructor_name}
                  </h2>
                  <p className="text-sm text-brand-navy mb-4">Aspiritech Academy Instructor</p>
                  {course.instructor_bio && (
                    <p className="text-slate-600 leading-relaxed mb-4 whitespace-pre-line">
                      {course.instructor_bio}
                    </p>
                  )}
                  {course.instructor_website && (
                    <a
                      href={course.instructor_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-brand-navy hover:text-brand-navy transition-colors font-medium"
                      data-testid="instructor-website-link"
                    >
                      <Globe className="w-4 h-4" /> Visit My Website
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lesson Content Dialog */}
      <Dialog open={!!activeLesson} onOpenChange={(open) => !open && setActiveLesson(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="lesson-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{activeLesson?.title}</DialogTitle>
            {activeLesson?.description && <p className="text-sm text-slate-500">{activeLesson.description}</p>}
          </DialogHeader>
          <div className="mt-2">
            {activeLesson && <LessonContent lesson={activeLesson} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CourseDetail;
